/**
 * THE SEA. The one sound a player hears for hours, and therefore the whole brief.
 *
 * -- WHAT IT IS -----------------------------------------------------------------
 *
 * Two loops of coprime length, generated here from seeded noise and played back as plain
 * looping buffers. Nothing oscillates, nothing filters, nothing modulates at play time: all of
 * that happens once, in this file, before a sample is heard. What runs while the game idles is
 * two buffer readers and one gain, which is the cheapest continuous sound the Web Audio API can
 * make and is why this design was chosen over the obvious one.
 *
 * THE OBVIOUS ONE, AND WHY IT IS REJECTED. The textbook browser sea is a noise source into a
 * bandpass into a gain, with two low-frequency oscillators moving the cutoff and the level.
 * That is five live nodes doing arithmetic every sample for as long as the tab is open, plus
 * two oscillators that never stop. The run's standing bar is near-zero idle cost - it is why
 * this game has no React and no render loop - and a graph that computes a filter forever fails
 * it on principle before anybody measures it. Baking the same result into a buffer costs one
 * pass at unlock and nothing afterwards.
 *
 * -- SYNTHESISED, NOT SAMPLED, AND THAT IS THE LICENCE ANSWER --------------------
 *
 * There is not one recorded byte in this lane. Every sample the game plays is computed by this
 * repository from a seed written in spec.ts, so there is no provenance question to answer, no
 * attribution file to keep correct, and no chance of a CC0 claim on a sample turning out to be
 * somebody else uploading a recording they did not own. That risk is real and it is
 * unresolvable after the fact; synthesis removes it rather than managing it.
 *
 * -- THE THREE THINGS THAT MAKE A CONTINUOUS BED IRRITATING ----------------------
 *
 * Named, because each one is answered by a specific decision here rather than by taste.
 *
 *   1. HISS. Energy above about 5 kHz is what fatigues a listener over an hour. Both bands are
 *      capped by cascaded one-poles and the measured spectral centroid is reported by
 *      `measure.ts` rather than asserted here.
 *   2. A PITCH. Any resonance in the chain becomes a note that cannot be escaped. There is no
 *      resonant filter anywhere in this lane - see the last section of filters.ts.
 *   3. A RECOGNISABLE PERIOD. Handled twice over: the loops do not click (they are periodic by
 *      construction, not crossfaded) and they do not line up (17 and 11 seconds, realigning at
 *      187). What is left to recognise is a three-minute pattern in moving water.
 *
 * A fourth, and it is the one this file spends the most effort on: MACHINE MODULATION. A bed
 * whose whole spectrum rises and falls together is an air conditioner. The surface is therefore
 * two bands with separate, differently-shaped breathing - see SURFACE in spec.ts.
 */

import { circularHighpass, circularLowpass, normaliseRms, periodicEnvelope } from './filters';
import { makeRng } from './rng';
import { SEEDS, SURFACE, SWELL } from './spec';

/** Seeded white noise, uniform in [-1, 1). The starting point for every band here. */
function whiteNoise(length: number, seed: number): Float32Array {
  const rng = makeRng(seed);
  const out = new Float32Array(length);
  for (let i = 0; i < length; i++) out[i] = rng.bipolar();
  return out;
}

/** One band: noise, a highpass, a cascade of lowpasses, all around the ring. */
function band(
  length: number,
  sampleRate: number,
  seed: number,
  highpassHz: number,
  lowpassHz: number,
  poles: number,
): Float32Array {
  const buf = whiteNoise(length, seed);
  circularHighpass(buf, highpassHz, sampleRate);
  for (let p = 0; p < poles; p++) circularLowpass(buf, lowpassHz, sampleRate);
  return buf;
}

/** Multiply in place by a periodic envelope of the same length. Stays periodic. */
function applyEnvelope(buf: Float32Array, env: Float32Array): void {
  for (let i = 0; i < buf.length; i++) buf[i] = (buf[i] as number) * (env[i] as number);
}

/** A rendered layer, ready to become an AudioBuffer. */
export interface SeaLayer {
  /** One entry per channel. Length is the same for all of them. */
  readonly channels: readonly Float32Array[];
  /** Samples per channel. The loop period is this over the sample rate. */
  readonly length: number;
  readonly sampleRate: number;
  /** Seconds the loop takes. Derived, and reported so a caller can check the coprimality. */
  readonly seconds: number;
}

/**
 * THE SWELL - one mono channel of everything under about 120 Hz.
 *
 * It carries no detail, which is the point: it is the weight under the surface, and on a laptop
 * speaker most of it will not reproduce at all. That is fine. On anything with a woofer or on
 * headphones it is the difference between a hiss and a sea.
 */
export function renderSwell(sampleRate: number): SeaLayer {
  const length = Math.round(SWELL.seconds * sampleRate);
  const buf = band(length, sampleRate, SEEDS.swell, SWELL.highpassHz, SWELL.lowpassHz, SWELL.lowpassPoles);
  applyEnvelope(buf, periodicEnvelope(length, SWELL.envelopePartials, SWELL.envelopePhases, SWELL.envelopeDepth));
  normaliseRms(buf, SWELL.rms);
  return { channels: [buf], length, sampleRate, seconds: length / sampleRate };
}

/**
 * THE SURFACE - stereo, two bands, two independent breaths.
 *
 * Left and right come from different noise seeds through identical filters, so the two sides
 * have the same spectrum and no correlation. That is what a wide field is: not a delay, not a
 * pan, not a phase trick that collapses when somebody listens in mono, just two draws from the
 * same distribution.
 *
 * The two ENVELOPES are shared across the channels rather than drawn per side. If the left and
 * right breathed independently the image would wander, which reads as a fault rather than as
 * space. One breath, two textures.
 */
/** The two shared breaths, computed once and used by both channels. */
export function surfaceEnvelopes(length: number): { low: Float32Array; high: Float32Array } {
  return {
    low: periodicEnvelope(length, SURFACE.low.envelopePartials, SURFACE.low.envelopePhases, SURFACE.low.envelopeDepth),
    high: periodicEnvelope(length, SURFACE.high.envelopePartials, SURFACE.high.envelopePhases, SURFACE.high.envelopeDepth),
  };
}

const SURFACE_SEEDS: readonly (readonly [number, number])[] = [
  [SEEDS.surfaceLowLeft, SEEDS.surfaceHighLeft],
  [SEEDS.surfaceLowRight, SEEDS.surfaceHighRight],
];

/**
 * ONE SIDE of the surface, before the pair is levelled.
 *
 * Exposed on its own so the engine can render the two sides in two separate tasks. That is not
 * decomposition for its own sake: the whole render measured 774 ms of blocked main thread on the
 * player's first click, and the fix is partly to make each piece cheaper and partly to stop any
 * one of them from owning the thread long enough to drop more than a frame.
 */
export function renderSurfaceSide(
  sampleRate: number,
  side: number,
  envelopes: { low: Float32Array; high: Float32Array },
): Float32Array {
  const length = envelopes.low.length;
  const seeds = SURFACE_SEEDS[side % SURFACE_SEEDS.length] as readonly [number, number];
  const low = band(length, sampleRate, seeds[0], SURFACE.low.highpassHz, SURFACE.low.lowpassHz, SURFACE.low.lowpassPoles);
  const high = band(length, sampleRate, seeds[1], SURFACE.high.highpassHz, SURFACE.high.lowpassHz, SURFACE.high.lowpassPoles);
  applyEnvelope(low, envelopes.low);
  applyEnvelope(high, envelopes.high);
  // Each band is levelled on its own before the sum, so the gain in spec.ts is a real
  // band balance and not a number that also happens to be correcting a filter loss.
  normaliseRms(low, 1);
  normaliseRms(high, 1);
  const out = new Float32Array(length);
  for (let i = 0; i < length; i++) {
    out[i] = (low[i] as number) * SURFACE.low.gain + (high[i] as number) * SURFACE.high.gain;
  }
  return out;
}

/**
 * Level the two sides TOGETHER, from their summed power.
 *
 * Not each side to the stated RMS: two decorrelated channels each at -30.5 dBFS sum to a field
 * 3 dB hotter than the number anybody wrote down, and that is exactly the class of mistake that
 * makes a mix louder than its own spec sheet.
 */
export function levelSurfacePair(channels: readonly Float32Array[]): number {
  let sum = 0;
  let count = 0;
  for (const ch of channels) {
    for (let i = 0; i < ch.length; i++) sum += (ch[i] as number) ** 2;
    count += ch.length;
  }
  const rms = Math.sqrt(sum / Math.max(1, count));
  const g = rms > 0 ? SURFACE.rms / rms : 0;
  for (const ch of channels) for (let i = 0; i < ch.length; i++) ch[i] = (ch[i] as number) * g;
  return g;
}

export function renderSurface(sampleRate: number): SeaLayer {
  const length = Math.round(SURFACE.seconds * sampleRate);
  const envelopes = surfaceEnvelopes(length);
  const channels = SURFACE_SEEDS.map((_, side) => renderSurfaceSide(sampleRate, side, envelopes));
  levelSurfacePair(channels);
  return { channels, length, sampleRate, seconds: length / sampleRate };
}

/** How many channels the surface has, so a chunked caller can size its own loop. */
export const SURFACE_CHANNELS = SURFACE_SEEDS.length;

/** Both layers, in the order they are mixed. */
export function renderSea(sampleRate: number): readonly SeaLayer[] {
  return [renderSwell(sampleRate), renderSurface(sampleRate)];
}
